import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
    ChevronDown,
    ChevronRight,
    Circle,
    PlayCircle
} from 'lucide-react'
import { contentApi } from '@/api/content'
import classNames from 'classnames'

interface LessonSidebarProps {
    courseId: number
    currentLessonId: number
    className?: string
}

export function LessonSidebar({ courseId, currentLessonId, className }: LessonSidebarProps) {
    const [openModules, setOpenModules] = useState<Record<number, boolean>>({})

    const { data: course, isLoading } = useQuery({
        queryKey: ['course', courseId],
        queryFn: () => contentApi.getCourseDetail(courseId),
        enabled: !!courseId,
        staleTime: 10 * 60 * 1000, // 10분 캐시
    })

    // 현재 레슨이 속한 모듈을 자동으로 펼침
    useEffect(() => {
        if (course && currentLessonId) {
            const currentModule = course.modules.find(m =>
                m.lessons.some(l => l.id === currentLessonId)
            )

            if (currentModule) {
                setOpenModules(prev => ({
                    ...prev,
                    [currentModule.id]: true
                }))
            }
        }
    }, [course, currentLessonId])

    const toggleModule = (moduleId: number) => {
        setOpenModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }))
    }

    if (isLoading) {
        return (
            <div className={classNames("w-80 bg-gray-50 border-r border-gray-200 h-full p-4", className)}>
                <div className="h-6 bg-gray-200 rounded-sm w-3/4 mb-6 animate-pulse" />
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="space-y-2">
                            <div className="h-10 bg-gray-200 rounded-sm animate-pulse" />
                            <div className="h-6 bg-gray-200 rounded-sm w-1/2 ml-4 animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!course) return null

    return (
        <div className={classNames("w-80 bg-gray-50 border-r border-gray-200 h-full flex flex-col", className)}>
            {/* Course Header */}
            <div className="p-5 border-b border-gray-200 bg-white">
                <h2 className="font-bold text-gray-900 leading-tight">
                    {course.title}
                </h2>
                <div className="mt-2 text-xs text-gray-500 font-medium tracking-wide">
                    CORE COURSE
                </div>
            </div>

            {/* Modules List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="py-2">
                    {course.modules.map((module) => {
                        const isOpen = openModules[module.id]

                        return (
                            <div key={module.id} className="mb-1">
                                {/* Module Header */}
                                <button
                                    onClick={() => toggleModule(module.id)}
                                    className={classNames(
                                        "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-100",
                                        { "bg-gray-100": isOpen }
                                    )}
                                >
                                    <div className="shrink-0 text-gray-400">
                                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-gray-800 truncate">
                                            {module.title}
                                        </div>
                                        {/* <div className="text-xs text-gray-500 mt-0.5">
                      {module.lessons.length} Lessons
                    </div> */}
                                    </div>
                                </button>

                                {/* Lessons List */}
                                {isOpen && (
                                    <div className="bg-white border-y border-gray-100">
                                        {module.lessons.map((lesson) => {
                                            const isCurrent = lesson.id === currentLessonId

                                            return (
                                                <Link
                                                    key={lesson.id}
                                                    to={`/learn/lesson/${lesson.id}`}
                                                    className={classNames(
                                                        "group flex items-start gap-3 pl-11 pr-4 py-3 transition-all hover:bg-gray-50 relative",
                                                        {
                                                            "bg-indigo-50 hover:bg-indigo-50 text-indigo-900": isCurrent,
                                                            "text-gray-600": !isCurrent
                                                        }
                                                    )}
                                                >
                                                    {isCurrent && (
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
                                                    )}

                                                    <div className={classNames("mt-0.5", {
                                                        "text-indigo-600": isCurrent,
                                                        "text-gray-400 group-hover:text-gray-600": !isCurrent
                                                    })}>
                                                        {isCurrent ? (
                                                            <PlayCircle className="w-4 h-4 fill-indigo-100" />
                                                        ) : (
                                                            <Circle className="w-4 h-4" />
                                                        )}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className={classNames("text-sm transition-colors", {
                                                            "font-bold text-indigo-700": isCurrent,
                                                            "font-medium group-hover:text-gray-900": !isCurrent
                                                        })}>
                                                            {lesson.title}
                                                        </div>
                                                        <div className="mt-1 flex items-center gap-2">
                                                            {lesson.estimatedDurationMinutes && (
                                                                <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded-sm text-gray-500">
                                                                    {lesson.estimatedDurationMinutes}분
                                                                </span>
                                                            )}
                                                            {/* TODO: 완료 여부 표시는 나중에 사용자 진행 상황 연동 필요 */}
                                                            {/* <span className="text-[10px] text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> 완료
                              </span> */}
                                                        </div>
                                                    </div>
                                                </Link>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
